import type { ManifestarNfsePayload, ManifestarNfseResultado } from '../types';

const MANIFESTAR_NFSE_URL =
  'https://services.impostograma.com.br/captura-prod/v1/impostograma/nfse/manifestacoes';

/** O navegador não completou a chamada: rede, ou a pré-checagem de CORS recusada (conferência da SPEC-011, §3). */
export const MENSAGEM_SEM_RESPOSTA =
  'Não foi possível falar com o serviço de manifestação da NFS-e. Confira os eventos da nota antes de tentar de novo.';

interface CorpoDaManifestacao {
  success?: boolean;
  data?: ManifestarNfseResultado | null;
  errors?: unknown;
  message?: unknown;
}

/**
 * A manifestação continua indo direto ao serviço de captura: a chamada pelo servidor está parada por decisão de
 * outro sistema (SPEC-011, "O que para", item 1). O que muda é que a tela passa a mostrar o que o serviço disse, em
 * vez de uma frase fixa:
 * - erro do serviço: `{ success: false, errors: [...] }`;
 * - recusa do ambiente nacional: o serviço responde `success: true` com `data.sucesso: false` e `data.erros`, às
 *   vezes com HTTP 2xx — por isso o status HTTP sozinho não basta;
 * - chamada que não se completa: o `fetch` falha antes de haver resposta.
 */
export async function manifestarNfseApi(
  payload: ManifestarNfsePayload
): Promise<ManifestarNfseResultado | null> {
  let response: Response;
  try {
    response = await fetch(MANIFESTAR_NFSE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new Error(MENSAGEM_SEM_RESPOSTA);
  }

  const corpo = await lerCorpo(response);
  const resultado = corpo?.data ?? null;

  if (!response.ok || corpo?.success === false || resultado?.sucesso === false) {
    const mensagens = [
      ...comoMensagens(corpo?.errors),
      ...comoMensagens(resultado?.erros),
      ...comoMensagens(corpo?.message),
    ];
    throw new Error(
      mensagens.length > 0
        ? mensagens.join(' ')
        : `O serviço de manifestação recusou o pedido (HTTP ${response.status}).`
    );
  }

  return resultado;
}

async function lerCorpo(response: Response): Promise<CorpoDaManifestacao | null> {
  try {
    const texto = await response.text();
    if (!texto) return null;
    const corpo: unknown = JSON.parse(texto);
    return corpo && typeof corpo === 'object' ? (corpo as CorpoDaManifestacao) : null;
  } catch {
    return null;
  }
}

/**
 * As mensagens como texto. O serviço transforma em JSON o erro do ambiente nacional que não é texto
 * (`asStringArray`); quando é um objeto com descrição, mostra-se a descrição e o código, e não o JSON.
 */
export function comoMensagens(valor: unknown): string[] {
  const itens = Array.isArray(valor) ? valor : valor === undefined || valor === null ? [] : [valor];

  return itens
    .map(item => (typeof item === 'string' ? descreverTexto(item) : descreverObjeto(item)))
    .filter((mensagem): mensagem is string => !!mensagem && mensagem.trim() !== '');
}

function descreverTexto(texto: string): string {
  try {
    const objeto: unknown = JSON.parse(texto);
    return descreverObjeto(objeto) ?? texto;
  } catch {
    return texto;
  }
}

function descreverObjeto(objeto: unknown): string | null {
  if (!objeto || typeof objeto !== 'object') return null;

  const campos = objeto as Record<string, unknown>;
  const descricao = [
    campos.Descricao,
    campos.descricao,
    campos.Mensagem,
    campos.mensagem,
    campos.message,
  ].find(valor => typeof valor === 'string' && valor.trim() !== '') as string | undefined;
  const codigo = [campos.Codigo, campos.codigo].find(
    valor => typeof valor === 'string' || typeof valor === 'number'
  );

  if (!descricao) return JSON.stringify(objeto);
  return codigo !== undefined ? `${codigo} - ${descricao}` : descricao;
}
